import { ProcedureStatus, WaypointType } from '@caresend/types';
import dayjs from 'dayjs';
import update from 'immutability-helper';
import { getServiceRegionFromLocation } from './location.js';
import { sampleFailedOrIncomplete } from './sample.js';
import { arrayToObj, keyValueArrayToObj, byIdArrayToObj, deduplicateArray } from '../languageExtensions/array.js';
import { objectMap, objectKeys, objToKeyValueArray } from '../languageExtensions/object.js';
import { nullishFilter, isNullish } from '../typeValidation/nullish.js';
import '../typeValidation/booking.js';
import '../typeValidation/draftWaypoint.js';
import '../typeValidation/feedback.js';
import '../environment/crypto/index.js';
import '../formatters/string.js';
import '../formatters/location.js';
import '../initializers/user.js';
import '../typeValidation/note.js';
import '../typeValidation/procedure.js';
import '../typeValidation/samples.js';
import '../typeValidation/shift.js';
import '../typeValidation/shipmentInfo.js';
import '../typeValidation/supply.js';
import '../typeValidation/timeSlot.js';
import '../typeValidation/waypoint.js';
import '../typeValidation/waypointAction.js';

const getWaypointFromWaypointData = waypointData => {
  const waypoint = update(waypointData, {
    $unset: ['waypointActionsData', 'itineraryData', 'placeData', 'placeGroupData']
  });
  return waypoint;
};
const getWaypointActionFromWaypointActionData = waypointActionData => {
  const waypointAction = update(waypointActionData, {
    $unset: ['waypointData']
  });
  return waypointAction;
};
const getWaypointActionsFromWaypointActionsData = waypointActionsData => objectMap(waypointActionsData, waypointActionData => getWaypointActionFromWaypointActionData(waypointActionData));
/**
 * Returns time zone of first procedure. If none is found, returns the
 * user's guessed timezone.
 */

const orderDataToTimeZone = orderData => {
  var _a, _b, _c, _d, _e;

  const firstBooking = Object.values((_a = orderData.bookingsData) !== null && _a !== void 0 ? _a : {})[0];
  const firstProcedure = Object.values((_b = firstBooking === null || firstBooking === void 0 ? void 0 : firstBooking.proceduresData) !== null && _b !== void 0 ? _b : {})[0];
  const timeZone = (_e = (_d = (_c = firstProcedure === null || firstProcedure === void 0 ? void 0 : firstProcedure.dateInfo) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.dateInstruction) === null || _e === void 0 ? void 0 : _e.timeZone;
  return timeZone !== null && timeZone !== void 0 ? timeZone : dayjs.tz.guess();
};
/**
 * Takes `OrderData` for multiple orders, and returns orders by ID, with
 * additional data stripped, and `OrderData` by ID.
 */

const orderDataToOrders = orderData => {
  const ordersData = arrayToObj(orderData, 'id');
  const extraKeysObj = {
    bookingsData: true,
    draftWaypointsData: true,
    officeData: true,
    orderFlowData: true,
    prescriberData: true,
    schedulingUserData: true,
    transactionsData: true
  };
  const orders = objectMap(ordersData, order => update(order, {
    $unset: objectKeys(extraKeysObj)
  }));
  return {
    orders,
    ordersData
  };
};
const bookingsDataToBookings = bookingsData => {
  const extraKeysObj = {
    orderData: true,
    patientData: true,
    proceduresData: true
  };
  return objectMap(bookingsData, booking => update(booking, {
    $unset: objectKeys(extraKeysObj)
  }));
};
/**
 * Takes `OrderData` for multiple orders, and returns child bookings by ID,
 * with additional data stripped, and child `BookingData` by ID.
 *
 * Optionally pass `withProcedureIDs` to only return bookings that contain
 * procedures with the given IDs.
 */

const orderDataToBookings = (orderData, withProcedureIDs) => {
  const resultArray = orderData.map(order => {
    var _a;

    const allBookingsData = (_a = order.bookingsData) !== null && _a !== void 0 ? _a : {};
    const bookingsDataArray = Object.values(allBookingsData).filter(booking => {
      var _a;

      if (!withProcedureIDs) return true;
      const procedureIDs = Object.keys((_a = booking.proceduresData) !== null && _a !== void 0 ? _a : {});
      const procedureIdMatched = procedureIDs.some(id => withProcedureIDs.includes(id));
      return procedureIdMatched;
    });
    const bookingsData = arrayToObj(bookingsDataArray, 'id');
    const bookings = bookingsDataToBookings(bookingsData);
    return {
      bookings,
      bookingsData
    };
  }).filter(nullishFilter);
  const result = resultArray.reduce((combined, singleOrder) => ({
    bookings: Object.assign(Object.assign({}, combined.bookings), singleOrder.bookings),
    bookingsData: Object.assign(Object.assign({}, combined.bookingsData), singleOrder.bookingsData)
  }), {
    bookings: {},
    bookingsData: {}
  });
  return result;
};
/**
 * Given `ProcedureData` objects that have `bookingData` as a property, returns
 * the bookings.
 */

const proceduresDataToBookings = proceduresData => {
  const bookingsDataArray = Object.values(proceduresData).map(procedure => procedure.bookingData).filter(nullishFilter);
  return bookingsDataToBookings(arrayToObj(bookingsDataArray, 'id'));
};
const draftWaypointDataToDecryptedDraftWaypoint = draftWaypointData => {
  const extraKeysObj = {
    contactData: true,
    draftProceduresData: true,
    itineraryData: true,
    prescriberData: true,
    proceduresData: true,
    supplyTransfersData: true,
    waypointData: true
  };
  return update(draftWaypointData, {
    $unset: objectKeys(extraKeysObj)
  });
};

const transformToSupplyInstructionConfigOptions = supplyInstructionConfig => ({
  supplyInstructionsByPlaceGroup: {
    [supplyInstructionConfig.placeGroupID]: {
      supplyInstructionsByDeliveryType: {
        [supplyInstructionConfig.samplesDeliveryType]: supplyInstructionConfig
      },
      samplesDeliveryType: supplyInstructionConfig.samplesDeliveryType
    }
  }
});
/**
 * Given `ProcedureData` objects that have `supplyInstructionConfigOptionsData`
 * as a property, returns the sets of options. If there are no options, but
 * a config was previously set on the procedure, it will be transformed into
 * a single option.
 */


const proceduresDataToSupplyInstructionConfigOptions = proceduresData => {
  const proceduresDataKeyVal = objToKeyValueArray(proceduresData);
  const optionsDataArray = proceduresDataKeyVal.map(({
    key,
    value: procedure
  }) => {
    const {
      supplyInstructionConfig,
      supplyInstructionConfigOptionsData
    } = procedure;
    let options = null;

    if (supplyInstructionConfigOptionsData) {
      options = supplyInstructionConfigOptionsData;
    } else if (supplyInstructionConfig) {
      options = transformToSupplyInstructionConfigOptions(supplyInstructionConfig);
    }

    if (!options) return null;
    return {
      key,
      value: options
    };
  }).filter(nullishFilter);
  return keyValueArrayToObj(optionsDataArray);
};
/**
 * - Orders: returns all procedures.
 * - Bookings: Pass booking ID, returns all procedures on the booking.
 * - Procedures: Pass booking ID (optional) and procedure ID(s), returns the
 *   procedure(s).
 */

const orderDataToProcedureData = (orderData, bookingID, procedureIDs) => {
  var _a;

  const bookings = Object.values((_a = orderData.bookingsData) !== null && _a !== void 0 ? _a : {});
  const filteredBookings = bookings.filter(booking => {
    if (!bookingID) return true;
    return booking.id === bookingID;
  });
  const procedures = filteredBookings.map(booking => {
    var _a;

    return Object.values((_a = booking.proceduresData) !== null && _a !== void 0 ? _a : {});
  }).flat();
  const procedureIDsArray = Array.isArray(procedureIDs) || isNullish(procedureIDs) ? procedureIDs : [procedureIDs];
  const filteredProcedures = procedures.filter(procedure => {
    if (!procedureIDsArray) return true;
    return procedureIDsArray.includes(procedure.id);
  });
  return filteredProcedures;
};
const proceduresDataToProcedures = proceduresData => {
  const extraKeysObj = {
    bookingData: true,
    contactData: true,
    draftWaypointData: true,
    nurseData: true,
    orderData: true,
    placeAccountsData: true,
    placeGroupsData: true,
    supplyInstructionConfigOptionsData: true,
    supplyItemsData: true,
    waypointActionsData: true
  };
  return objectMap(proceduresData, procedure => update(procedure, {
    $unset: objectKeys(extraKeysObj)
  }));
};
/**
 * Takes `BookingData` by ID, and returns child procedures by ID, with
 * additional data stripped, and child `ProcedureData` by ID.
 */

const bookingsDataToProcedures = bookingsData => {
  const procedureDataByIdArray = Object.values(bookingsData).map(booking => booking.proceduresData).filter(nullishFilter);
  const proceduresData = byIdArrayToObj(procedureDataByIdArray);
  const procedures = proceduresDataToProcedures(proceduresData);
  return {
    procedures,
    proceduresData
  };
};
/**
 * Get Prescriber from DraftWaypointData
 */

const getPrescriberDataFromDraftWaypointData = draftWaypointData => {
  var _a;

  const {
    proceduresData
  } = draftWaypointData;
  const orderData = (_a = Object.values(proceduresData !== null && proceduresData !== void 0 ? proceduresData : {})[0]) === null || _a === void 0 ? void 0 : _a.orderData;
  return orderData === null || orderData === void 0 ? void 0 : orderData.prescriberData;
};
/**
 * Receives `ByID<ProcedureData>`
 *
 * Gets patients data from bookings
 *
 * Returns `ByID<EncryptedUser>`
 */

const getPatientsFromProceduresData = proceduresData => {
  /**
   * Get booking data from procedures data
   */
  const bookingsData = Object.values(proceduresData !== null && proceduresData !== void 0 ? proceduresData : {}).map(procedureData => procedureData.bookingData).filter(nullishFilter);
  /**
   * Get patients data from booking data
   */

  const patientsData = bookingsData.map(bookingData => bookingData.patientData).filter(nullishFilter);
  /**
   * Convert patients data to ByID<EncryptedUser>
   */

  return arrayToObj(patientsData, patientData => patientData.id);
};
/**
 * Takes `ProcedureData` by ID, and returns linked draft waypoints by ID.
 */

const proceduresDataToDecryptedDraftWaypoints = proceduresData => {
  const draftWaypoints = Object.values(proceduresData).map(procedure => procedure.draftWaypointData).filter(nullishFilter);
  const draftWaypointsByID = arrayToObj(draftWaypoints, 'id');
  return draftWaypointsByID;
};
/**
 * Pass `OrderData[]` and its associated `ByID<ProcedureData>` to get
 * nurse and prescriber `StaffUser`s by ID.
 */

const orderAndProceduresDataToStaffUsers = (orderData, proceduresData) => {
  const nursesArray = Object.values(proceduresData).map(procedureData => procedureData.nurseData).filter(nullishFilter);
  const contactData = Object.values(proceduresData).map(procedureData => procedureData.contactData).filter(nullishFilter);
  const prescribersArray = orderData.map(orderDataPoint => orderDataPoint.prescriberData).filter(nullishFilter); // Convert to obj to remove duplicates

  const staffUsersObj = arrayToObj([...nursesArray, ...prescribersArray, ...contactData], 'id');
  return staffUsersObj;
};
/**
 * Pass `BookingData` by ID to get patients by ID.
 */

const bookingsDataToPatients = bookingsData => {
  const patientsArray = Object.values(bookingsData).map(bookingData => bookingData.patientData).filter(nullishFilter); // Convert to obj to remove duplicates

  const usersObj = arrayToObj(patientsArray, 'id');
  return usersObj;
};
/**
 * Pass `OrderData` to get patients by ID.
 *
 * Optionally pass `withProcedureIDs` to only return patients from bookings that
 * contain procedures with the given IDs.
 */

const orderDataToPatients = (orderData, withProcedureIDs) => {
  const {
    bookingsData
  } = orderDataToBookings([orderData], withProcedureIDs);
  return bookingsDataToPatients(bookingsData);
};
/**
 * Pass `OrderData[]`, associated `ByID<ProcedureData>`, and associated
 * `ByID<BookingData>` to get patients, nurses and prescribers by id.
 */

const orderBookingAndProcedureDataToUsers = (orderData, bookingsData, proceduresData) => {
  const patients = bookingsDataToPatients(bookingsData);
  const staffUsers = orderAndProceduresDataToStaffUsers(orderData, proceduresData);
  return Object.assign(Object.assign({}, patients), staffUsers);
};
/**
 * Returns true if:
 *
 * - Procedure was completed
 * - All samples completed
 * - No sample collection or drop-off failures
 * - All nurse processing instructions were reported as followed correctly
 *   by the nurse.
 */

const isProcedureFulfilledWithoutFaults = procedureData => {
  var _a;

  const completed = procedureData.status === ProcedureStatus.COMPLETED;
  if (!completed) return false;
  const samples = Object.values((_a = procedureData.samples) !== null && _a !== void 0 ? _a : {});
  const someSampleFailed = samples.some(sampleFailedOrIncomplete);
  return !someSampleFailed;
};
/**
 * Returns true if all procedures were completed with no collection or
 * drop-off failures and any nurse processing instructions were reported
 * as followed correctly by the nurse.
 */

const isOrderFulfilledWithoutFaults = orderData => {
  const procedures = orderDataToProcedureData(orderData);
  const someProcedureFailed = procedures.some(procedure => !isProcedureFulfilledWithoutFaults(procedure));
  return !someProcedureFailed;
};
/**
 * - Orders: returns all waypoints.
 * - Bookings: Pass booking ID, returns all waypoints on the booking.
 * - Procedures: Pass booking ID and procedure ID, returns the patient waypoint
 *   on the procedure.
 */

const orderDataToPatientWaypointData = (orderData, bookingID, procedureID) => {
  const procedures = orderDataToProcedureData(orderData, bookingID, procedureID);
  const waypointActions = procedures.map(procedure => {
    var _a;

    return Object.values((_a = procedure.waypointActionsData) !== null && _a !== void 0 ? _a : {});
  }).flat();
  const waypoints = waypointActions.map(waypointAction => waypointAction.waypointData).filter(waypoint => (waypoint === null || waypoint === void 0 ? void 0 : waypoint.type) === WaypointType.PATIENT);
  return waypoints;
};
/**
 * - Orders: returns all service regions.
 * - Bookings: Pass booking ID, returns all service regions on the booking.
 * - Procedures: Pass booking ID and procedure ID, returns the service region
 *   from the patient waypoint on the procedure.
 * - Draft Orders, Bookings, Procedures: follows same as above if there are
 *   final service regions. All fallback to globally set `serviceRegionID`
 *   in `data` if present.
 *
 * Note: it is expected that an order will only have one service region
 * associated with it, but all found service regions will be returned.
 */

const orderDataToServiceRegionNames = async (orderData, variables, getValueOnce, bookingID, procedureID) => {
  /**
   * Waypoint will be used as source of location if it exists. Otherwise,
   * procedure will be used.
   */
  let sourcesWithLocation;
  const waypoints = orderDataToPatientWaypointData(orderData, bookingID, procedureID);

  if (waypoints.length) {
    sourcesWithLocation = waypoints;
  } else {
    const procedures = orderDataToProcedureData(orderData, bookingID, procedureID);
    sourcesWithLocation = procedures.map(({
      location
    }) => location ? {
      location
    } : undefined).filter(nullishFilter);
  }

  const regions = await Promise.all(sourcesWithLocation.map(({
    location
  }) => !location ? undefined : getServiceRegionFromLocation(location, variables, getValueOnce)).filter(nullishFilter));
  const regionNames = regions.map(region => region === null || region === void 0 ? void 0 : region.name).filter(nullishFilter);
  return deduplicateArray(regionNames);
};

export { bookingsDataToBookings, bookingsDataToPatients, bookingsDataToProcedures, draftWaypointDataToDecryptedDraftWaypoint, getPatientsFromProceduresData, getPrescriberDataFromDraftWaypointData, getWaypointActionFromWaypointActionData, getWaypointActionsFromWaypointActionsData, getWaypointFromWaypointData, isOrderFulfilledWithoutFaults, isProcedureFulfilledWithoutFaults, orderAndProceduresDataToStaffUsers, orderBookingAndProcedureDataToUsers, orderDataToBookings, orderDataToOrders, orderDataToPatientWaypointData, orderDataToPatients, orderDataToProcedureData, orderDataToServiceRegionNames, orderDataToTimeZone, proceduresDataToBookings, proceduresDataToDecryptedDraftWaypoints, proceduresDataToProcedures, proceduresDataToSupplyInstructionConfigOptions };
